import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Pencil } from '@factorialco/f0-react/icons/app'
import {
  LocationsEmployeesCollectionListDocument,
  LocationsEmployeesCollectionListQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type LocationEmployee =
  LocationsEmployeesCollectionListQuery['employees']['employeesConnection']['nodes'][number] & {
    lastLocation: boolean
  }

const managedByEmployeeEnum = {
  managed_by_me: 'managed_by_me',
  managed_by_others: 'managed_by_others',
  managed_directly_by_me: 'managed_directly_by_me',
  managed_timeoff_by_me: 'managed_timeoff_by_me',
}

const EmployeesComponent = ({ children }: React.PropsWithChildren) => {
  const { company, employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const client = useApolloClient()
  const currentEmployeeId = currentEmployee?.id

  const { filters: employeeFilters } = useEmployeeFilters()

  const { locationId } = useOutletContext<{ locationId: number }>()

  const source = useGraphqlDataCollectionSource(
    {
      document: LocationsEmployeesCollectionListDocument,
      mapVariables: ({ search, sortings, filters }) => {
        const teamIds = filters.team?.map((id) => id)
        // const jobRoleIds = filters.jobRole?.map((id) => id)
        const onlyMyReportees =
          filters.directReports &&
          filters.directReports.length > 0 &&
          (filters.directReports.includes(managedByEmployeeEnum.managed_by_me) ||
            filters.directReports.includes(managedByEmployeeEnum.managed_by_others))
            ? filters.directReports?.includes(managedByEmployeeEnum.managed_by_me)
            : undefined
        const directReports =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes(managedByEmployeeEnum.managed_directly_by_me)
            ? true
            : undefined
        const managedTimeoffByMe =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes(managedByEmployeeEnum.managed_timeoff_by_me)
            ? [currentEmployeeId ?? 0]
            : undefined

        return {
          companyId: company.id,
          onlyMyReportees,
          reportingTo: filters.reportingTo?.map((id) => id),
          onlyDirectReports: directReports,
          timeoffManagerIds: managedTimeoffByMe,
          onlyActive: filters.active?.includes(true),
          sort: sortings?.[0],
          search: search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: [locationId],
        }
      },
      mapPagination: (data) => {
        return {
          total: data.employees.employeesConnection.totalCount ?? 0,
        }
      },
      mapData: (data) => {
        return data.employees.employeesConnection.nodes.map((node) => ({
          ...node,
          lastLocation: data.locations.locationsConnection.totalCount === 1,
        }))
      },
      itemUrl: (item: LocationEmployee) => navigation.employees.id.fullPath({ id: item.id }),
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
        'referenceContract.jobTitle': {
          label: i18n.t('common.job'),
        },
        startsOn: {
          label: i18n.t('employee_form.hire_date'),
        },
        active: {
          label: i18n.t('employee_form.status'),
          defaultValue: true,
        },
      },
      currentFilters: {
        active: [true],
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      filters: {
        team: employeeFilters.team,
        active: {
          type: 'in',
          label: i18n.t('locations.settings.employees.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('locations.settings.employees.filters.only_active'),
                value: true,
              },
            ],
          },
        },
        directReports: {
          type: 'in',
          label: i18n.t('locations.settings.employees.filters.managed_by_me'),
          options: {
            options: [
              {
                label: i18n.t('locations.settings.employees.filters.managed_by_me_option'),
                value: managedByEmployeeEnum.managed_by_me,
              },
              {
                label: i18n.t('locations.settings.employees.filters.managed_by_others'),
                value: managedByEmployeeEnum.managed_by_others,
              },
              {
                label: i18n.t('locations.settings.employees.filters.managed_directly_by_me_option'),
                value: managedByEmployeeEnum.managed_directly_by_me,
              },
              {
                label: i18n.t('locations.settings.employees.filters.managed_timeoff_by_me_option'),
                value: managedByEmployeeEnum.managed_timeoff_by_me,
              },
            ],
          },
        },
        reportingTo: employeeFilters.reportingTo,
      },
      primaryActions: () => ({
        label: i18n.t('locations.settings.employees.add.cta'),
        icon: Add,
        onClick: () => {
          navigate(
            navigation.settings.locations.locationsId.employees.add.fullPath({ id: locationId })
          )
        },
      }),
      selectable: (item: LocationEmployee) => item.id,
      itemActions: (item: LocationEmployee) => [
        {
          label: i18n.t('settings.locations.location.employees.actions.assign'),
          icon: Pencil,
          disabled: item.lastLocation,
          onClick: () => {
            navigate(
              navigation.settings.locations.locationsId.employees.moveEmployeeId.fullPath({
                id: locationId,
                employeeId: item.id,
              })
            )
          },
        },
      ],
    },
    [company.id]
  )

  return (
    <ResponsivePage.Body variant="wide">
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('locations.settings.employees.first_name'),
                  sorting: 'firstName',
                  render: (item: LocationEmployee) => ({
                    type: 'person',
                    value: {
                      firstName: item.firstName,
                      lastName: '',
                      avatar: item.access.avatar,
                    },
                  }),
                },
                {
                  label: i18n.t('locations.settings.employees.last_name'),
                  sorting: 'lastName',
                  render: (item: LocationEmployee) => item.lastName,
                },
                {
                  label: i18n.t('locations.settings.employees.job'),
                  render: (item: LocationEmployee) => item.referenceContract?.jobTitle ?? '',
                },
                {
                  label: i18n.t('locations.settings.employees.hire_date'),
                  render: (item: LocationEmployee) => item.referenceContract?.startsOn ?? '',
                },
                {
                  label: i18n.t('locations.settings.employees.status_name'),
                  render: (item: LocationEmployee) => ({
                    type: 'status',
                    value: {
                      label: item.active
                        ? i18n.t('locations.settings.employees.status.active')
                        : i18n.t('locations.settings.employees.status.inactive'),
                      status: item.active ? 'positive' : 'neutral',
                    },
                  }),
                },
              ],
            },
          },
        ]}
      />
      {children}
    </ResponsivePage.Body>
  )
}

export default EmployeesComponent
